/** biome-ignore-all lint/complexity/noExcessiveLinesPerFunction: too many lines */
/** biome-ignore-all lint/style/useNamingConvention: allow snake case */
import cx from 'classnames';
import {useCallback, useEffect, useState} from 'react';
import {shallowEqual, useSelector, useStore} from 'react-redux';
import {saveEventThunk} from '~/CreateFabricInvitation/api/event';
import {NavBottom} from '~/CreateFabricInvitation/components/NavBottom/NavBottom';
import {OptionToggle} from '~/CreateFabricInvitation/components/OptionToggle';
import {navigateToRoute} from '~/CreateFabricInvitation/navigation';
import {
  selectEventType,
  selectIsPremiumTemplate,
} from '~/CreateFabricInvitation/reducers/fabricTemplate/selectors';
import {
  SET_UPSELL_CONFIRMED,
  selectIsUpsellConfirmed,
} from '~/CreateFabricInvitation/reducers/modals';
import {webViewPendingUpgradeToPremium} from '~/CreateFabricInvitation/webview/webview';
import {PledgelingOverlay} from '~/common/fabric/CreateDesign/components/EventGifting/PledgelingOverlay';
import {Registries} from '~/common/fabric/CreateDesign/components/EventGifting/Registries';
import {useCharityCopy, useCrowdfundCopy} from '~/common/fabric/CreateDesign/pledge_copy';
import {webViewModalShowing} from '~/common/fabric/hooks';
import {UPDATE_EVENT_DETAILS} from '~/common/fabric/reducers/details/constants';
import {
  selectEventType as selectEventEventType,
  selectEventStatus,
} from '~/common/fabric/reducers/eviteEvent/selectors';
import {
  UPDATE_GIFT_OPTIONS,
  UPDATE_PLEDGELING_OPTIONS,
} from '~/common/fabric/reducers/giftingOptions/constants';
import * as selectors from '~/common/fabric/reducers/giftingOptions/selectors';
import {
  selectGiftingOptionsUpgraded,
  selectGiftOptions,
} from '~/common/fabric/reducers/giftingOptions/selectors';
import {UpsellModal} from '~/common/fabric/UpsellModal/UpsellModal';
import {selectIsProOrPartyPass} from '~/common/fabric/User/reducers/user';
import {isJson} from '~/common/utils';
import {isDraft} from '~/common/utils/event_utils';
import {
  TURN_OFF_DONATION_LOADING,
  TURN_ON_DONATION_LOADING,
} from '~/ViewInvitation/reducers/constants';
import {Button} from '~library/atoms/Button';
import {Divider} from '~library/atoms/Divider';
import {Typography} from '~library/atoms/Typography';
import {DonationIcon} from '~library/svg/DonationIcon';
import {GiftEditIcon} from '~library/svg/GiftEditIcon';
import {HeartCheckIcon} from '~library/svg/HeartCheckIcon';

import './EventGifting.sass';

const DonationSummaryLink = ({setShowOverlay}) => (
  <Button
    ariaLabel="Edit Charity"
    as="a"
    variant="link"
    size="small"
    data-qa-id="donationSummaryLink"
    onClick={() => setShowOverlay('charity-donation')}
  >
    Edit
  </Button>
);

const CrowdfundLink = ({setShowOverlay}) => (
  <Button
    ariaLabel="Edit Crowdfund"
    as="a"
    variant="link"
    size="small"
    data-qa-id="crowdfundLink"
    onClick={() => setShowOverlay('crowd-funding')}
  >
    Edit
  </Button>
);

export const EventGifting = ({inPreviewStep = false}) => {
  const store = useStore();

  const [showOverlay, setShowOverlay] = useState('');
  const [showUpsellModal, setShowUpsellModal] = useState(false);
  const [toToggleOff, setToToggleOff] = useState('');
  const [nextButtonClicked, setNextButtonClicked] = useState(false);

  const isPremiumTemplate = useSelector(selectIsPremiumTemplate);

  const charityId = useSelector(selectors.selectCharityID);
  const purpose = useSelector(selectors.selectCrowdfundPurpose);
  const showGifting = useSelector(selectors.selectShowGifting);

  const giftOptionsCount = useSelector(selectors.selectGiftOptionsCount);
  const upsellConfirmed = useSelector(selectIsUpsellConfirmed);

  const evtDetails = useSelector((state) => state.details, shallowEqual);
  const eventStatus = useSelector(selectEventStatus);
  const eventType = useSelector(selectEventEventType);

  const giftingOptionsUpgraded = useSelector(selectGiftingOptionsUpgraded);
  const giftingOptions = useSelector(selectGiftOptions);

  const isProOrPartyPass = useSelector(selectIsProOrPartyPass);

  const isFuneral = eventType === 'funeral_memorial_service';
  const templateEventType = useSelector(selectEventType);

  const charityCopy = useCharityCopy(templateEventType);
  const crowdfundCopy = useCrowdfundCopy(templateEventType);

  const eventIsDraft = isDraft(eventStatus);

  const handleNavButtonClick = useCallback(() => {
    setNextButtonClicked(true);
  }, []);

  useEffect(() => {
    const searchParams = new URLSearchParams(window.location.search);
    const defaultGiftingOption = searchParams.get('option');
    if (defaultGiftingOption === 'charity-donation' || defaultGiftingOption === 'crowd-funding') {
      setShowOverlay(defaultGiftingOption);
    }
  }, []);

  // biome-ignore lint/correctness/useExhaustiveDependencies: deps
  useEffect(() => {
    const listener = async (e) => {
      if (e.origin.includes('pledge') && isJson(e.data)) {
        const {action, data: payload} = JSON.parse(e.data);

        const embedCodes = document.querySelectorAll('.plg-search, .plg-crowd');
        const embedCode = Array.from(embedCodes).find(
          (code) => code.firstElementChild.contentWindow === e.source
        );

        switch (action) {
          case 'updateEvent': {
            // Turn on donation loading spinner
            store.dispatch({type: TURN_ON_DONATION_LOADING});

            store.dispatch({
              type: UPDATE_PLEDGELING_OPTIONS,
              payload,
            });
            await store.dispatch(saveEventThunk(true, 5000));
            // Turn off donation loading spinner
            store.dispatch({type: TURN_OFF_DONATION_LOADING});

            break;
          }

          case 'removeEvent': {
            if ([...embedCode.classList].includes('plg-search')) {
              store.dispatch({
                type: UPDATE_PLEDGELING_OPTIONS,
                payload: {beneficiary_type: 'Organization'},
              });
            } else {
              store.dispatch({
                type: UPDATE_PLEDGELING_OPTIONS,
                payload: {beneficiary_type: 'Crowdfund'},
              });
            }
            await store.dispatch(saveEventThunk(true));

            break;
          }
          default:
            break;
        }
      }
    };

    if (
      evtDetails.isPremium &&
      !isPremiumTemplate &&
      !showGifting &&
      giftingOptionsUpgraded === false
    ) {
      store.dispatch({
        type: UPDATE_GIFT_OPTIONS,
        payload: {
          upgraded: true,
        },
      });

      store.dispatch({
        type: UPDATE_EVENT_DETAILS,
        payload: {isPremium: true},
      });
    }

    window.addEventListener('message', listener);
    return () => window.removeEventListener('message', listener);
  }, []);

  const upsellConfirm = async () => {
    store.dispatch({
      type: SET_UPSELL_CONFIRMED,
      payload: true,
    });
    store.dispatch({
      type: UPDATE_EVENT_DETAILS,
      payload: {isPremium: true, hideUpsell: true},
    });
    switch (toToggleOff) {
      case 'giftRegistry':
        await store.dispatch({
          type: UPDATE_GIFT_OPTIONS,
          payload: {gift_registry_urls: []},
        });
        break;

      case 'charityDonation':
        await store.dispatch({
          type: UPDATE_PLEDGELING_OPTIONS,
          payload: {beneficiary_type: 'Organization'},
        });
        break;

      case 'crowdFunding':
        await store.dispatch({
          type: UPDATE_PLEDGELING_OPTIONS,
          payload: {beneficiary_type: 'Crowdfund'},
        });
        break;

      case 'giftIdeas':
        await store.dispatch({
          type: UPDATE_GIFT_OPTIONS,
          payload: {
            showGifting: false,
            options: {
              ...giftingOptions,
              event_option_show_gifting: false,
            },
          },
        });
        break;
      case 'registries':
        store.dispatch({
          type: UPDATE_GIFT_OPTIONS,
          payload: {
            gift_registry_urls: [],
            gift_registry_name: '',
          },
        });
        break;
      default:
        break;
    }

    await store.dispatch({
      type: UPDATE_GIFT_OPTIONS,
      payload: {
        upgraded: true,
      },
    });

    setToToggleOff('');
    // Don't save here if in edit mode; only save if in create flow
    await store.dispatch(saveEventThunk(eventIsDraft));
    webViewPendingUpgradeToPremium(true);
    window.dataLayer.push({
      event: 'fabric_create_upgrade_gifting',
    });
  };

  const closeOverlay = async (overlayTitle) => {
    setShowOverlay('');
    // Ensure registry changes are saved on save
    if (overlayTitle === 'gift-registry') {
      await store.dispatch(saveEventThunk());
    }
  };

  const backClicked = async () => {
    setShowOverlay('');
  };

  const toggleUpsellModal = () => {
    if (!showUpsellModal) {
      webViewModalShowing('upsell');
    }
    setShowUpsellModal(!showUpsellModal);
  };

  const shouldShowUpsellModal = () =>
    !isPremiumTemplate && !evtDetails.isPremium && !isProOrPartyPass && giftOptionsCount === 1;

  return (
    <div className={cx('create-flow__options event-gifting', {'in-preview-step': inPreviewStep})}>
      <form
        className="create-flow__form-container"
        autoComplete="off"
        onSubmit={(e) => e.preventDefault()}
      >
        <div className={cx('create-flow__form')}>
          {!isFuneral && (
            <Registries
              nextButtonClicked={nextButtonClicked}
              onRegistryDelete={() => {
                setToToggleOff('registries');
                setShowUpsellModal(true);
              }}
            />
          )}
          <Divider marginTop="1rem" marginBottom="0.5rem" />
          <Typography
            className="event-gifting__description"
            variant="header4"
            data-qa-id="giftRegistryDescription"
          >
            More Gifting Options
          </Typography>
          <OptionToggle
            dataQaId="charity-toggle"
            checked={!!charityId}
            eviteExclusive
            option="charityDonation"
            icon={<DonationIcon />}
            heading={charityCopy.header}
            description={
              charityId ? (
                <DonationSummaryLink setShowOverlay={setShowOverlay} />
              ) : (
                charityCopy.subHeader
              )
            }
            onChange={async () => {
              if (charityId) {
                if (shouldShowUpsellModal()) {
                  setToToggleOff('charityDonation');
                  toggleUpsellModal();
                } else {
                  store.dispatch({
                    type: UPDATE_PLEDGELING_OPTIONS,
                    payload: {beneficiary_type: 'Organization'},
                  });
                  await store.dispatch(saveEventThunk());
                }
              } else {
                setShowOverlay('charity-donation');
              }
            }}
          />
          <OptionToggle
            dataQaId="crowdfund-toggle"
            checked={!!purpose}
            eviteExclusive
            option="crowdFunding"
            icon={<HeartCheckIcon />}
            heading={crowdfundCopy.header}
            description={
              purpose ? <CrowdfundLink setShowOverlay={setShowOverlay} /> : crowdfundCopy.subHeader
            }
            onChange={async () => {
              if (purpose) {
                if (shouldShowUpsellModal()) {
                  setToToggleOff('crowdFunding');
                  toggleUpsellModal();
                } else {
                  store.dispatch({
                    type: UPDATE_PLEDGELING_OPTIONS,
                    payload: {beneficiary_type: 'Crowdfund'},
                  });
                  await store.dispatch(saveEventThunk());
                }
              } else {
                setShowOverlay('crowd-funding');
              }
            }}
          />
          {!isFuneral && (
            <OptionToggle
              dataQaId="gifting-toggle"
              checked={showGifting}
              option="giftIdeas"
              icon={<GiftEditIcon />}
              heading="Gift Ideas"
              description="Include Evite's top gift suggestions available on Amazon and Walmart."
              onChange={async () => {
                if (showGifting) {
                  if (shouldShowUpsellModal()) {
                    setToToggleOff('giftIdeas');
                    toggleUpsellModal();
                  } else {
                    store.dispatch({
                      type: UPDATE_GIFT_OPTIONS,
                      payload: {
                        showGifting: false,
                        options: {
                          ...giftingOptions,
                          event_option_show_gifting: false,
                        },
                      },
                    });
                    // Don't save here if in edit mode; only save if in create flow
                    await store.dispatch(saveEventThunk(eventIsDraft));
                  }
                } else {
                  store.dispatch({
                    type: UPDATE_GIFT_OPTIONS,
                    payload: {
                      showGifting: true,
                      options: {
                        ...giftingOptions,
                        event_option_show_gifting: true,
                      },
                    },
                  });
                  // Don't save here if in edit mode; only save if in create flow
                  await store.dispatch(saveEventThunk(eventIsDraft));
                }
              }}
            />
          )}
        </div>
        {!eventIsDraft ? (
          <div className="create-flow__form-nav">
            <Button
              className="create-flow__form-nav-done"
              data-qa-id="nav-bottom-done"
              variant="primary"
              size="large"
              type="button"
              onClick={async () => {
                if (upsellConfirmed) {
                  await navigateToRoute('review', store);
                } else {
                  await navigateToRoute('exit', store);
                }
              }}
            >
              {`${upsellConfirmed ? 'Next: Checkout' : 'Done'}`}
            </Button>
          </div>
        ) : (
          !inPreviewStep && (
            <NavBottom
              handleBackButtonClick={handleNavButtonClick}
              handleNextButtonClick={handleNavButtonClick}
            />
          )
        )}
      </form>
      <PledgelingOverlay
        isOpen={!!showOverlay}
        overlayTitle={showOverlay}
        closeOverlay={closeOverlay}
        backClicked={backClicked}
        charityAdded={!!charityId}
        crowdfundAdded={!!purpose}
      />
      {showUpsellModal && !evtDetails.isPremium && (
        <UpsellModal
          noAutoRestore
          toggleUpsellModal={() => toggleUpsellModal()}
          onConfirmAction={() => upsellConfirm()}
          closeOverlay={() => {
            webViewPendingUpgradeToPremium(false);
            toggleUpsellModal();
          }}
          mode="gifting"
        />
      )}
    </div>
  );
};
